
import React, { useState, useEffect } from 'react';
import { Shield, Monitor, Megaphone, Hourglass, CalendarOff, Plus, Trash2, Save, RefreshCw, AlertTriangle } from 'lucide-react';
import Swal from 'sweetalert2';
import { apiRequest } from '../services/api';
import { ConfigEntry } from '../types';
import { LS_AUTH_KEY, DEFAULT_SITE_TITLE, DEFAULT_FORM_TITLE, DEFAULT_TIME_SLOTS, DEFAULT_TOPIC_OPTIONS, WEEK_DAYS } from '../constants';

interface ConfigManagerProps {
  onConfigChange?: () => Promise<void>;
}

export const ConfigManager: React.FC<ConfigManagerProps> = ({ onConfigChange }) => {
  const [configs, setConfigs] = useState<ConfigEntry[]>([]);
  const [loading, setLoading] = useState(false);

  // Form states
  const [newType, setNewType] = useState('WEEKDAY');
  const [newVal, setNewVal] = useState('');
  const [newValEnd, setNewValEnd] = useState('');
  const [newNote, setNewNote] = useState('');
  const [selectedWeekdays, setSelectedWeekdays] = useState<string[]>([]);

  // Global setting states
  const [siteTitle, setSiteTitle] = useState('');
  const [formTitle, setFormTitle] = useState('');
  const [timeSlots, setTimeSlots] = useState('');
  const [topicOptions, setTopicOptions] = useState('');
  const [notice, setNotice] = useState('');
  const [minNotice, setMinNotice] = useState('0');
  const [maxAdvance, setMaxAdvance] = useState('0');
  const [notifyEmail, setNotifyEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');

  const loadData = async () => {
    setLoading(true);
    const res = await apiRequest<ConfigEntry[]>('getConfigs');
    if (res.success && res.data) {
      setConfigs(res.data);
      const find = (t: string) => res.data!.find(c => c.type === t)?.value || '';
      
      setSiteTitle(find('SITE_TITLE') || DEFAULT_SITE_TITLE);
      setFormTitle(find('FORM_TITLE') || DEFAULT_FORM_TITLE);
      setTimeSlots(find('TIME_SLOTS') || DEFAULT_TIME_SLOTS);
      
      const topics = find('TOPIC_OPTION') || find('TOPIC_OPTIONS') || DEFAULT_TOPIC_OPTIONS;
      setTopicOptions(topics);
      
      setNotice(find('NOTICE'));
      setMinNotice(find('MIN_NOTICE') || '0');
      
      const advance = find('MAX_ADVANCE') || find('MAX_ADVANCE_DAYS') || '0';
      setMaxAdvance(advance);
    }

    const sysRes = await apiRequest<{ notifyEmail: string }>('getSystemSettings');
    if (sysRes.success && sysRes.data) {
      setNotifyEmail(sysRes.data.notifyEmail);
    }
    setLoading(false);
  };

  useEffect(() => { loadData(); }, []);

  const handleTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setNewType(e.target.value);
    setNewVal('');
    setNewValEnd('');
    setSelectedWeekdays([]);
  };

  const handleUpdate = async (action: string, body: any, successTitle: string) => {
    setLoading(true);
    const res = await apiRequest(action, 'POST', body);
    setLoading(false);
    if (res.success) {
      Swal.fire({
        icon: 'success',
        title: successTitle,
        timer: 1500,
        showConfirmButton: false
      });
      if (body.newPassword) {
        localStorage.removeItem(LS_AUTH_KEY);
        window.location.reload();
      }
      if (onConfigChange) await onConfigChange();
      loadData();
    } else {
      Swal.fire({ icon: 'error', title: '儲存失敗', text: res.message });
    }
  };

  const handleAddConfig = async () => {
    let finalValue = newVal;
    if (newType === 'WEEKDAY') {
      if (!newVal) return Swal.fire({ icon: 'warning', title: '未選擇星期', text: '請至少選擇一個公休日' });
    }
    if (newType === 'RANGE') finalValue = `${newVal},${newValEnd}`;
    if (newType === 'RANGE_WEEKDAY') {
      if (!newVal || !newValEnd || selectedWeekdays.length === 0) {
        return Swal.fire({ icon: 'warning', title: '資訊不完整', text: '請填寫日期範圍並選擇星期' });
      }
      finalValue = `${newVal},${newValEnd},${selectedWeekdays.join(',')}`;
    }
    const res = await apiRequest('addConfig', 'POST', { type: newType, value: finalValue, note: newNote });
    if (res.success) {
      setNewVal(''); setNewValEnd(''); setNewNote(''); setSelectedWeekdays([]);
      if (onConfigChange) await onConfigChange();
      loadData();
    }
  };

  const handleDelConfig = async (id: string) => {
    const result = await Swal.fire({
      title: '確定刪除規則？',
      text: "刪除後將立即恢復開放該時段的預約。",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#e11d48',
      cancelButtonColor: '#94a3b8',
      confirmButtonText: '確定刪除',
      cancelButtonText: '取消'
    });

    if (result.isConfirmed) {
      const res = await apiRequest('delConfig', 'POST', { id });
      if (res.success) {
        if (onConfigChange) await onConfigChange();
        loadData();
        Swal.fire({ icon: 'success', title: '已刪除', timer: 1000, showConfirmButton: false });
      }
    }
  };

  const toggleWeekday = (d: number) => {
    const ds = String(d);
    const current = newVal ? newVal.split(',').filter(x => x !== '') : [];
    const updated = current.includes(ds) ? current.filter(x => x !== ds) : [...current, ds].sort();
    setNewVal(updated.join(','));
  };

  const toggleRangeWeekday = (d: number) => {
    const ds = String(d);
    setSelectedWeekdays(prev => prev.includes(ds) ? prev.filter(x => x !== ds) : [...prev, ds].sort());
  };

  const translateValue = (type: string, val: string) => {
    if (!val) return '未設定';
    if (type === 'WEEKDAY') return val.split(',').map(v => `週${WEEK_DAYS[Number(v)]}`).join(', ');
    if (type === 'RANGE') return val.replace(',', ' 至 ');
    if (type === 'RANGE_WEEKDAY') {
      const parts = val.split(',');
      if (parts.length < 3) return val;
      const [start, end, ...days] = parts;
      return `${start} ~ ${end} (每${days.map(d => `週${WEEK_DAYS[Number(d)]}`).join(', ')})`;
    }
    return val;
  };

  const sectionClass = "bg-white p-6 rounded-3xl border border-slate-200 shadow-sm transition-all hover:shadow-md";
  const labelClass = "block text-xs font-bold text-slate-400 uppercase tracking-widest mb-2";
  const inputClass = "w-full p-3 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-blue-500 transition-all text-sm font-semibold";
  const btnClass = "px-6 py-2.5 bg-slate-800 text-white rounded-xl font-bold hover:bg-slate-900 transition-all text-sm disabled:opacity-50 flex items-center gap-2 shadow-sm";

  const systemKeys = [
    'NOTICE', 'MIN_NOTICE', 'MAX_ADVANCE', 'MAX_ADVANCE_DAYS', 
    'SITE_TITLE', 'FORM_TITLE', 'TIME_SLOTS', 'TOPIC_OPTION', 
    'TOPIC_OPTIONS', 'NOTIFY_EMAIL', 'ADMIN_PASSWORD'
  ];

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pb-20 animate-fade-in">
      <div className={sectionClass}>
        <h3 className="text-lg font-bold mb-6 flex items-center gap-3 text-slate-800">
          <Monitor className="w-5 h-5 text-blue-500" /> 網站視覺與基礎設定
        </h3>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>網站標題</label>
              <input type="text" className={inputClass} value={siteTitle} onChange={e => setSiteTitle(e.target.value)} />
            </div>
            <div>
              <label className={labelClass}>表單標題</label>
              <input type="text" className={inputClass} value={formTitle} onChange={e => setFormTitle(e.target.value)} />
            </div>
          </div>
          <div>
            <label className={labelClass}>預約時段 (以半形逗號分隔)</label>
            <input type="text" className={inputClass} value={timeSlots} onChange={e => setTimeSlots(e.target.value)} />
          </div>
          <div>
            <label className={labelClass}>諮詢主題選項 (以半形逗號分隔，留空則為文字輸入)</label>
            <input type="text" className={inputClass} value={topicOptions} onChange={e => setTopicOptions(e.target.value)} placeholder="例：Excel,Word,Google表單" />
          </div>
          <div className="flex justify-end pt-2">
            <button disabled={loading} onClick={() => handleUpdate('saveGlobalSettings', { siteTitle, formTitle, timeSlots, topicOptions }, '基礎設定已更新')} className={btnClass}>
              <Save className="w-4 h-4" /> 儲存設定
            </button>
          </div>
        </div>
      </div>

      <div className={sectionClass}>
        <h3 className="text-lg font-bold mb-6 flex items-center gap-3 text-slate-800">
          <Shield className="w-5 h-5 text-rose-500" /> 系統安全與通知
        </h3>
        <div className="space-y-4">
          <div>
            <label className={labelClass}>通知收件信箱</label>
            <input type="email" className={inputClass} value={notifyEmail} onChange={e => setNotifyEmail(e.target.value)} />
          </div>
          <div>
            <label className={labelClass}>變更管理密碼 (如不變更請留空)</label>
            <input type="password" className={inputClass} placeholder="新密碼" value={newPassword} onChange={e => setNewPassword(e.target.value)} />
          </div>
          <div className="flex justify-end pt-2">
            <button disabled={loading} onClick={() => handleUpdate('saveSystemSettings', { notifyEmail, newPassword: newPassword || undefined }, '安全設定已更新')} className={btnClass}>
              <Save className="w-4 h-4" /> 儲存安全設定
            </button>
          </div>
        </div>
      </div>

      <div className={`${sectionClass} md:col-span-2`}>
        <h3 className="text-lg font-bold mb-6 flex items-center gap-3 text-slate-800">
          <Megaphone className="w-5 h-5 text-amber-500" /> 預約頁面公告說明
        </h3>
        <textarea 
          className={`${inputClass} h-32 resize-none font-medium`} 
          placeholder="顯示於預約表單上方的說明文字..." 
          value={notice} 
          onChange={e => setNotice(e.target.value)} 
        />
        <div className="flex justify-end mt-4">
          <button disabled={loading} onClick={() => handleUpdate('saveNotice', { value: notice }, '說明文字已更新')} className={btnClass}>
            <Save className="w-4 h-4" /> 儲存公告
          </button>
        </div>
      </div>

      <div className={sectionClass}>
        <h3 className="text-lg font-bold mb-6 flex items-center gap-3 text-slate-800">
          <Hourglass className="w-5 h-5 text-emerald-500" /> 預約限制規則
        </h3>
        <div className="grid grid-cols-2 gap-6">
          <div className="space-y-2">
            <label className={labelClass}>最少提前天數</label>
            <div className="flex items-center gap-2">
              <input type="number" min="0" className={`${inputClass} text-center`} value={minNotice} onChange={e => setMinNotice(e.target.value)} />
              <span className="text-xs text-slate-400 font-bold whitespace-nowrap">天前</span>
            </div>
          </div>
          <div className="space-y-2">
            <label className={labelClass}>開放天數範圍</label>
            <div className="flex items-center gap-2">
              <input type="number" min="0" className={`${inputClass} text-center`} value={maxAdvance} onChange={e => setMaxAdvance(e.target.value)} />
              <span className="text-xs text-slate-400 font-bold whitespace-nowrap">天內</span>
            </div>
          </div>
        </div>
        <div className="flex justify-end mt-4">
          <button disabled={loading} onClick={async () => {
            const hasMaxAdvanceDays = configs.some(c => c.type === 'MAX_ADVANCE_DAYS');
            const key = hasMaxAdvanceDays ? 'saveMaxAdvanceDays' : 'saveMaxAdvance';
            await apiRequest('saveMinNotice', 'POST', { value: minNotice });
            await apiRequest(key, 'POST', { value: maxAdvance });
            if (onConfigChange) await onConfigChange();
            Swal.fire({ icon: 'success', title: '限制規則已更新', timer: 1500, showConfirmButton: false });
            loadData();
          }} className={btnClass}>
            <Save className="w-4 h-4" /> 儲存限制
          </button>
        </div>
      </div>

      <div className={`${sectionClass} md:row-span-2`}>
        <h3 className="text-lg font-bold mb-6 flex items-center gap-3 text-slate-800">
          <CalendarOff className="w-5 h-5 text-rose-400" /> 公休日與排程排除
        </h3>
        
        <div className="bg-slate-50 p-5 rounded-2xl border border-slate-200 mb-6 space-y-4">
          <div className="grid grid-cols-1 gap-4">
            <select className={inputClass} value={newType} onChange={handleTypeChange}>
              <option value="WEEKDAY">每週例行公休</option>
              <option value="DATE">單一日期公休</option>
              <option value="RANGE">連續期間公休</option>
              <option value="RANGE_WEEKDAY">特定期間內的特定星期</option>
            </select>
            {newType === 'WEEKDAY' && (
              <div className="flex flex-wrap gap-2">
                {[0, 1, 2, 3, 4, 5, 6].map(d => (
                  <button key={d} onClick={() => toggleWeekday(d)} className={`px-3 py-1.5 rounded-lg text-xs font-bold border transition-all ${newVal.split(',').includes(String(d)) ? 'bg-slate-800 text-white border-slate-800' : 'bg-white text-slate-600 border-slate-200'}`}>
                    週{WEEK_DAYS[d]}
                  </button>
                ))}
              </div>
            )}
            {newType === 'DATE' && <input type="date" className={inputClass} value={newVal} onChange={e => setNewVal(e.target.value)} />}
            {(newType === 'RANGE' || newType === 'RANGE_WEEKDAY') && (
              <div className="flex items-center gap-2">
                <input type="date" className={inputClass} value={newVal} onChange={e => setNewVal(e.target.value)} />
                <span className="text-slate-300">~</span>
                <input type="date" className={inputClass} value={newValEnd} onChange={e => setNewValEnd(e.target.value)} />
              </div>
            )}
            {newType === 'RANGE_WEEKDAY' && (
              <div className="flex flex-wrap gap-2 pt-2">
                {[0, 1, 2, 3, 4, 5, 6].map(d => (
                  <button key={d} onClick={() => toggleRangeWeekday(d)} className={`px-3 py-1.5 rounded-lg text-xs font-bold border transition-all ${selectedWeekdays.includes(String(d)) ? 'bg-slate-800 text-white border-slate-800' : 'bg-white text-slate-600 border-slate-200'}`}>
                    週{WEEK_DAYS[d]}
                  </button>
                ))}
              </div>
            )}
            <input type="text" placeholder="公休原因 (顯示於日曆)" className={inputClass} value={newNote} onChange={e => setNewNote(e.target.value)} />
          </div>
          <button onClick={handleAddConfig} disabled={loading} className="w-full bg-blue-600 text-white py-3 rounded-xl font-bold hover:bg-blue-700 transition-all flex items-center justify-center gap-2 shadow-lg shadow-blue-100">
            <Plus className="w-4 h-4" /> 新增排除規則
          </button>
        </div>

        <div className="space-y-3">
          {configs.filter(c => !systemKeys.includes(c.type)).map(c => (
            <div key={c.id} className="flex justify-between items-center p-4 bg-white border border-slate-100 rounded-2xl group hover:bg-slate-50 transition-all shadow-sm">
              <div>
                <div className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1">{c.type}</div>
                <div className="text-sm font-black text-slate-700">{translateValue(c.type, c.value)}</div>
                {c.note && <div className="text-xs text-rose-500 font-bold mt-1 italic flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> {c.note}</div>}
              </div>
              <button onClick={() => handleDelConfig(c.id)} className="p-2 text-rose-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-all opacity-0 group-hover:opacity-100">
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
